import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Badge } from '@mui/material';
import axios from 'axios';
import API_URL from '../config';

// Import Icons (Filled for active, Outlined for inactive)
import DashboardIcon from '@mui/icons-material/Dashboard';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import GavelIcon from '@mui/icons-material/Gavel';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import BarChartIcon from '@mui/icons-material/BarChart';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import MailIcon from '@mui/icons-material/Mail';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import PinDropIcon from '@mui/icons-material/PinDrop';
import PinDropOutlinedIcon from '@mui/icons-material/PinDropOutlined';
import ComputerIcon from '@mui/icons-material/Computer';
import ComputerOutlinedIcon from '@mui/icons-material/ComputerOutlined';
import StoreIcon from '@mui/icons-material/Store';
import StoreOutlinedIcon from '@mui/icons-material/StoreOutlined';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

// Import CSS and Logo
import '../assets/css/style.css';
import logoDark from '../assets/logo-dark.png';

function Layout({ logout }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [openGroups, setOpenGroups] = useState({});
  const [unassignedTickets, setUnassignedTickets] = useState(0);

  useEffect(() => {
      const fetchUnassigned = async () => {
          try {
              const token = localStorage.getItem('token');
              if (token) {
                  const res = await axios.get(`${API_URL}/tickets/stats/unassigned`, {
                      headers: { Authorization: `Bearer ${token}` }
                  });
                  setUnassignedTickets(res.data.count);
              }
          } catch (e) {
              console.error("Failed to fetch ticket stats", e);
          }
      };

      fetchUnassigned();
      const interval = setInterval(fetchUnassigned, 60000);
      return () => clearInterval(interval);
  }, []);

  const handleDrawerToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleToggleGroup = (title) => {
    setOpenGroups(prev => ({
      ...prev,
      [title]: prev[title] === false ? true : false
    }));
  };

  const handleLogout = (e) => {
    e.preventDefault();
    logout();
  };

  const fullName = localStorage.getItem('full_name') || 'User';
  const role = localStorage.getItem('user_role') || 'Member';
  const divisions = JSON.parse(localStorage.getItem('user_divisions') || '[]'); 
  // const initials = fullName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);

  const menuGroups = [
      {
          title: 'MAIN',
          items: [
              { text: 'Dashboard', iconActive: <DashboardIcon />, iconInactive: <DashboardOutlinedIcon />, path: '/', subtitle: 'Overview and metrics' },
              { text: 'Projects', iconActive: <FolderIcon />, iconInactive: <FolderOutlinedIcon />, path: '/projects', subtitle: 'Manage projects' },
              { text: 'Contracts', iconActive: <GavelIcon />, iconInactive: <GavelOutlinedIcon />, path: '/contracts', subtitle: 'Manage contracts' },
              { text: 'Hierarchy', iconActive: <AccountTreeIcon />, iconInactive: <AccountTreeOutlinedIcon />, path: '/team', subtitle: 'IT hierarchy' },
              { text: 'KPIs', iconActive: <BarChartIcon />, iconInactive: <BarChartOutlinedIcon />, path: '/kpis', subtitle: 'Performance metrics' },
              { text: 'Clients', iconActive: <BusinessIcon />, iconInactive: <BusinessOutlinedIcon />, path: '/clients', subtitle: 'Manage clients' },
              { text: 'References', iconActive: <MailIcon />, iconInactive: <MailOutlineIcon />, path: '/references', subtitle: 'Support emails' },
              { text: 'TimeSheet', iconActive: <AssignmentIcon />, iconInactive: <AssignmentOutlinedIcon />, path: '/datasheets', subtitle: 'Time sheet' },
              { text: 'Punching', iconActive: <PinDropIcon />, iconInactive: <PinDropOutlinedIcon />, path: '/punching', subtitle: 'Attendance logs' },
          ]
      },
      ...((role === 'Admin' || divisions.includes('IT') || divisions.includes('Software')) ? [{
          title: 'SUPPORT',
          items: [
              { text: 'IT Dashboard', iconActive: <ComputerIcon />, iconInactive: <ComputerOutlinedIcon />, path: '/support-dashboard', subtitle: 'IT Overview' },
              { text: 'IT Projects', iconActive: <AssignmentIcon />, iconInactive: <AssignmentOutlinedIcon />, path: '/it-projects', subtitle: 'Manage IT projects' },
              { text: 'IT Suppliers', iconActive: <StoreIcon />, iconInactive: <StoreOutlinedIcon />, path: '/it-suppliers', subtitle: 'Manage IT suppliers' },
              { text: 'Software Licenses', iconActive: <WorkspacePremiumIcon />, iconInactive: <WorkspacePremiumOutlinedIcon />, path: '/software-licenses', subtitle: 'Manage licenses' },
              { text: 'Tickets', iconActive: <ConfirmationNumberIcon />, iconInactive: <ConfirmationNumberOutlinedIcon />, path: '/tickets', subtitle: 'Manage support tickets' },
              { text: 'Inbox', iconActive: <MailIcon />, iconInactive: <MailOutlineIcon />, path: '/inbox', subtitle: 'Company Inbox' },
          ]
      }] : []),
      ...(role === 'Admin' ? [{
          title: 'SETUP',
          items: [
              { text: 'Setup', iconActive: <SettingsIcon />, iconInactive: <SettingsOutlinedIcon />, path: '/setup', subtitle: 'System configuration' }
          ]
      }] : [])
  ];

  return (
    <div className="app-layout">
        {/* Sidebar Navigation (Left) */}
        <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
            <div className="sidebar-header">
                <img src={logoDark} alt="ACTS" className="sidebar-logo" style={{ maxHeight: '50px', width: 'auto', maxWidth: '100%' }} />
                <button 
                    id="sidebarToggle" 
                    className="btn btn-sm btn-secondary" 
                    style={{ padding: '0.25rem 0.5rem', background: 'transparent', border: 'none', fontSize: '1.2rem', color: 'var(--text-muted)', cursor: 'pointer' }}
                    onClick={handleDrawerToggle}
                >
                    <i className="fas fa-bars"></i>
                </button>
            </div>

            <div className="sidebar-nav">
                {menuGroups.map((group, gIndex) => {
                    const isOpen = openGroups[group.title] !== false; // defaults to true
                    return (
                        <div className={`nav-group ${!isOpen ? 'is-closed' : ''}`} key={group.title}>
                            <div className="nav-group-header" onClick={() => handleToggleGroup(group.title)}>
                                <span>{group.title}</span>
                                <i className={`fas ${isOpen ? 'fa-chevron-up' : 'fa-chevron-down'} toggle-icon`}></i>
                            </div>
                            {isOpen && (
                            <ul className="nav-group-items">
                                {group.items.map((item) => {
                                    const isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
                                    return (
                                        <li className="sidebar-nav-item" key={item.text}>
                                            <a 
                                                href="#" 
                                                className={`sidebar-nav-link ${isActive ? 'active' : ''}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    navigate(item.path);
                                                }}
                                            >
                                                {item.path === '/tickets' && unassignedTickets > 0 ? (
                                                    <Badge badgeContent={unassignedTickets} color="error" style={{ marginRight: '1rem' }}>
                                                        <i className="menu-icon" style={{ margin: 0 }}>
                                                            {isActive ? item.iconActive : item.iconInactive}
                                                        </i>
                                                    </Badge>
                                                ) : (
                                                    <i className="menu-icon">
                                                        {isActive ? item.iconActive : item.iconInactive}
                                                    </i>
                                                )}
                                                <span>{item.text}</span>
                                            </a>
                                        </li>
                                    );
                                })}
                            </ul>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="sidebar-footer">
                <div className="sidebar-user-profile">
                    <div className="sidebar-avatar">
                        <i className="fas fa-user-tie"></i>
                    </div>
                    <div className="sidebar-user-details">
                        <span className="sidebar-username" id="displayUsername">{fullName}</span>
                        <span className="sidebar-user-role">{role}</span> 
                    </div>
                    <a href="#" className="sidebar-logout" title="Logout" onClick={handleLogout}> 
                        <i className="fas fa-sign-out-alt"></i>
                    </a>
                </div>
            </div>
        </aside>

        {/* Main Content Area */}
        <main className={`main-content ${sidebarCollapsed ? 'collapsed' : ''}`}>
            {/* The routed content gets dumped here */}
            <div className="container-fluid">
                <Outlet />
            </div>
        </main>
    </div>
  );
}

export default Layout; 
